const API_BASE = '/api.php';

export interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

// ==================== 分类 ====================
export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: string;
  sort_order: number;
  status: number;
}

// ==================== 产品 ====================
export interface ProductImage {
  id: number;
  product_id: number;
  url: string;
  sort_order: number;
}


export interface Product {
  id: number;
  category_id: number;
  name: string;
  tag: string;
  description: string;
  material: string;
  specifications: string;
  notes: string;
  sort_order: number;
  status: number;
  category_name: string;
  images: ProductImage[];
  created_at: string;
  updated_at: string;
}

export interface ProductListResult {
  items: Product[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==================== 博客 ====================
export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  category: string;
  author: string;
  read_time: string;
  excerpt: string;
  content: string;
  image: string;
  is_top: number;
  status: number;
  published_at: string;
  created_at: string;
  updated_at: string;
}

export interface BlogListResult {
  items: BlogPost[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==================== 留言 ====================
export interface ContactData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
}

// ==================== API 函数 ====================
async function request<T>(action: string, params: Record<string, string> = {}): Promise<T> {
  const query = new URLSearchParams({ action, ...params }).toString();
  const res = await fetch(`${API_BASE}?${query}`);
  const json: ApiResult<T> = await res.json();
  if (!json.success) {
    throw new Error(json.message || '请求失败');
  }
  return json.data as T;
}

/** 获取分类列表 */
export function fetchCategories(): Promise<Category[]> {
  return request<Category[]>('categories');
}

/** 获取产品列表 */
export function fetchProducts(page = 1, pageSize = 6, category?: string): Promise<ProductListResult> {
  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (category && category !== 'All') {
    params.category = category;
  }
  return request<ProductListResult>('products', params);
}

/** 获取产品详情 */
export function fetchProduct(id: number): Promise<Product> {
  return request<Product>('product', { id: String(id) });
}

/** 获取博客列表 */
export function fetchBlogPosts(page = 1, pageSize = 6, category?: string): Promise<BlogListResult> {
  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (category && category !== 'All') {
    params.category = category;
  }
  return request<BlogListResult>('blog_posts', params);
}

/** 获取博客详情 */
export function fetchBlogPost(id: number): Promise<BlogPost> {
  return request<BlogPost>('blog_post', { id: String(id) });
}

/** 获取博客分类 */
export function fetchBlogCategories(): Promise<{ id: number; name: string }[]> {
  return request<{ id: number; name: string }[]>('blog_categories');
}

/** 获取网站设置 */
export function fetchSettings(): Promise<Record<string, string>> {
  return request<Record<string, string>>('settings');
}

/** 提交留言 */
export async function submitContact(data: ContactData): Promise<{ id: number }> {
  const res = await fetch(`${API_BASE}?action=contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json: ApiResult<{ id: number }> = await res.json();
  if (!json.success) {
    throw new Error(json.message || '提交失败');
  }
  return json.data as { id: number };
}
